import { styled } from '@mui/material/styles';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CardHeader,
  Avatar,
  Grid,
  ImageList,
  Stack,
  Tooltip,
  Rating,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { CircleOutlined } from '@mui/icons-material';

export const MainContainer = styled(Box)({
  flexGrow: 1,
});

export const MainHeading = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.8rem',
    lineHeight: 1.2,
  },
}));

export const HelperText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const ExperienceCardContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
}));

export const CompanyAvatar = styled(Avatar)(
  ({ theme, logobackgroundcolor }) => ({
    width: 75,
    height: 75,
    backgroundColor: logobackgroundcolor,
    [theme.breakpoints.down('sm')]: {
      width: 50,
      height: 50,
    },
  })
);

export const CompanyTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
    lineHeight: 1.3,
  },
}));

export const CompanySubtitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    lineHeight: 1.4,
  },
}));

export const VisitSiteButton = styled(Button)({});

export const RoleTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
  },
}));

export const DetailsList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2),
}));

export const DetailsListItem = styled('li')(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

export const DetailsText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
}));

export const SkillsTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
  },
}));

export const SkillsImageList = styled(ImageList)(({ theme }) => ({
  paddingBottom: theme.spacing(0.5),
  // Preserve masonry layout when using component="div"
  display: 'block',
  columnCount: 'auto',
  columnGap: theme.spacing(1),
  '& > *': {
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
  },
}));

export const SkillDisplayCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(212, 225, 244, 0.8)',
  width: 'fit-content',
  minWidth: '100px',
  height: 'auto',
  margin: theme.spacing(0.625),
  padding: theme.spacing(0.625),
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0.25),
    padding: theme.spacing(0.375),
  },
}));

export const SkillDisplayStack = styled(Stack)({
  direction: 'row',
});

export const SkillNameText = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(0.375),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

export const SkillRating = styled(Rating)({
  color: '#d5a021',
  alignItems: 'center',
});

export const LanguageIconTooltip = styled(Tooltip)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}));

export const LanguageIconAvatar = styled(Avatar)(
  ({ theme, size = '100px' }) => ({
    width: size,
    height: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '60px',
      height: 'auto',
    },
  })
);

export const LearnMoreAccordionContainer = styled(Accordion)({
  width: '100%',
});

export const LearnMoreAccordionSummary = styled(AccordionSummary)(
  ({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
      '& .MuiAccordionSummary-content': {
        margin: 0,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.5),
        width: '100%',
      },
    },
  })
);

export const LearnMoreAccordionDetails = styled(AccordionDetails)({});

export const LearnMoreTitle = styled(Typography)(({ theme }) => ({
  width: '33%',
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginBottom: theme.spacing(0.5),
  },
}));

export const LearnMoreDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    lineHeight: 1.4,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
}));

export const RelevantLinksStack = styled(Stack)(({ theme }) => ({
  direction: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

export const RelevantLinksTitle = styled(Typography)({});

export const RelevantLinksButtonStack = styled(Stack)(({ theme }) => ({
  direction: 'row',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
  },
}));

export const RelevantLinkButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

export const ProjectsStack = styled(Stack)({
  direction: 'column',
});

export const ProjectsTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const ProjectCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(212, 225, 244, 0.8)',
  width: 'fit-content',
  minWidth: '100px',
  height: 'auto',
  margin: theme.spacing(0.625),
  padding: theme.spacing(0.625),
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  maxWidth: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '100%',
  },
}));

export const ProjectCardHeader = styled(CardHeader)({
  background: '#344f6b',
  color: 'WHITE',
  borderRadius: '4px 0px',
  margin: '-5px',
  boxShadow:
    '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
});

export const ProjectTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    lineHeight: 1.3,
  },
}));

export const ProjectSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.light,
}));

export const ProjectCardContent = styled(CardContent)(({ theme }) => ({
  maxWidth: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

export const ProjectToolsStack = styled(Stack)(({ theme }) => ({
  direction: 'row',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    gap: theme.spacing(0.25),
  },
}));

export const ProjectDetailsGrid = styled(Grid)({});

export const ProjectSectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
  },
}));

export const ProjectDetailsList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2),
}));

export const ProjectDetailsListItem = styled('li')(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

export const ProjectDetailsText = styled(Typography)(({ theme }) => ({
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    lineHeight: 1.5,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
}));

export const EmptyIcon = styled(CircleOutlined)({
  opacity: 0.55,
  color: '#493962',
});
